import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { ExternalLink } from '../../../../../../components/Clickables/ExternalLink/ExternalLink';
import { Heading } from '../../../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { MapIcon } from '../../../../../../components/Maps/components/MapIcon';
import { POINTS_OF_INTEREST } from '../../../../../../components/Maps/constants/mapLayers/pointsOfInterest';
import { Text } from '../../../../../../components/Text/Text';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { getPointOfInterest } from '@graphql/queries/rutilus/PointOfInterest';
import { useExplore } from '../../../../context/ExploreContext';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';

interface IProps {
  pointOfInterestId: number;
}

export const POIView = ({ pointOfInterestId }: IProps) => {
  const { exploreQueryParams } = useExplore();
  const { loading, data } = useRutilusQuery(getPointOfInterest, {
    variables: { id: pointOfInterestId },
  });
  const clientHasRendered = useClientHasRendered();

  if (clientHasRendered && !data && loading) {
    return (
      <MapDrawerLayout>
        <LoadingSpinner loadingText="Loading point of interest..." />
      </MapDrawerLayout>
    );
  }

  if (!data) {
    return (
      <MapDrawerLayout>
        <div className="flex h-full flex-col">
          <div className="flex">
            <Heading variant="sm" className="pr-2">
              Point Of Interest
            </Heading>
          </div>
          <MapDrawerNotification title="It looks like something went wrong" />
        </div>
      </MapDrawerLayout>
    );
  }

  const {
    address,
    city,
    country,
    county,
    description,
    email,
    primaryName,
    rampSurface,
    rampType,
    state,
    website,
    zipcode,
    phone,
    primaryType,
  } = data.entry;

  const pointOfInterest = Object.values(POINTS_OF_INTEREST).find(poi => poi.typeId === primaryType);

  return (
    <div className="grid h-full grid-cols-1 grid-rows-1">
      <MapDrawerLayout className="col-start-1 row-start-1 h-full">
        {pointOfInterest && (
          <MapIcon
            name={pointOfInterest.icon}
            alt="point of interest icon"
            mapStyle={exploreQueryParams.mapStyle}
            className="mt-2"
          />
        )}
        <div className="flex h-full flex-col pt-6">
          <div className="flex items-center">
            {primaryName && <Heading variant="xs">{primaryName}</Heading>}
          </div>

          <div>
            {pointOfInterest?.name && (
              <Text variant="sm" className="mt-2 mb-4">
                {pointOfInterest.name}
              </Text>
            )}
            {description && <Text className="m-0 mt-2">{description}</Text>}
            {rampType && <Text className="m-0 mt-2">Ramp Type: {rampType}</Text>}
            {rampSurface && <Text className="m-0 mt-2">Ramp Surface: {rampSurface}</Text>}
            {(address ?? city ?? state ?? zipcode ?? county ?? country) && (
              <Text className="m-0 mt-2">
                {address && (
                  <>
                    {address}
                    <br />
                  </>
                )}
                {city && state && zipcode && (
                  <>
                    {city}, {state} {zipcode}
                    <br />
                  </>
                )}
                {city && (!state || !zipcode) && (
                  <>
                    {city}
                    {state && `, ${state}`}
                    {zipcode && ` ${zipcode}`}
                    <br />
                  </>
                )}
                {county && (
                  <>
                    {county}
                    <br />
                  </>
                )}
                {country && <>{country}</>}
              </Text>
            )}
            {phone && <Text className="m-0 mt-2">{phone}</Text>}
            {email && (
              <Text className="m-0 mt-4 break-all">
                <ExternalLink href={`mailto:${email}`} target="_blank">
                  {email}
                </ExternalLink>
              </Text>
            )}
            {website && (
              <Text className="m-0 mt-4 break-all">
                <ExternalLink href={`https://${website}`} target="_blank">
                  {website}
                </ExternalLink>
              </Text>
            )}
          </div>
        </div>
      </MapDrawerLayout>
    </div>
  );
};
